/**
 * Retrieves the translation of text.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-i18n/
 */
import { __, sprintf } from '@wordpress/i18n';

/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';

/**
 * WordPress dependencies
 */
import { PanelBody, Spinner, SelectControl } from '@wordpress/components';
import { useEffect, useState } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { ColorPalette } from '@wordpress/block-editor';

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * Those files can contain any CSS code that gets applied to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import './editor.scss';

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @param {Object}   props               Block properties.
 * @param {Object}   props.attributes    Block attributes.
 * @param {Function} props.setAttributes Function to update block attributes.
 *
 * @return {Element} Element to render.
 */
export default function Edit( { attributes, setAttributes } ) {
	const { formId, theme, accentColor } = attributes;
	const [ preview, setPreview ] = useState( '' );
	const [ isLoading, setIsLoading ] = useState( false );
	const [ forms, setForms ] = useState( [] );
	const [ isLoadingForms, setIsLoadingForms ] = useState( true );

	const blockProps = useBlockProps();

	// Fetch available forms
	useEffect( () => {
		setIsLoadingForms( true );

		window.Groundhogg.stores.forms.fetchItems().then( items => {
			const formOptions = items.map( form => ( {
				label: `${form.name} (#${form.ID})`,
				value: String( form.ID ),
			} ) );
			setForms( [
				{ label: __( 'Select a form', 'gh-form-shortcode' ), value: '' },
				...formOptions,
			] );
			setIsLoadingForms( false );
		}).catch( () => {
			setForms( [
				{ label: __( 'No forms found', 'gh-form-shortcode' ), value: '' },
			] );
			setIsLoadingForms( false );
		} );
	}, [] );

	useEffect( () => {
		if ( ! formId ) {
			setPreview( '' );
			return;
		}

		setIsLoading( true );

		// Fetch the rendered shortcode preview
		apiFetch( {
			path: '/wp/v2/block-renderer/groundhogg/forms',
			method: 'POST',
			data: {
				attributes: {
					formId,
					theme,
					accentColor,
				},
				context: 'edit'
			},
		} )
			.then( ( response ) => {
				setPreview( response.rendered );
				setIsLoading( false );
			} )
			.catch( () => {
				setPreview( '' );
				setIsLoading( false );
			} );
	}, [ formId, theme, accentColor ] );

	return (
		<>
			<InspectorControls>
				<PanelBody
					title={ __( 'Form Settings', 'gh-form-shortcode' ) }
					initialOpen={ true }
				>
					{ isLoadingForms ? (
						<div style={{ padding: '12px', textAlign: 'center' }}>
							<Spinner />
							<p style={{ marginTop: '8px', fontSize: '13px', color: '#757575' }}>
								{ __( 'Loading forms...', 'groundhogg' ) }
							</p>
						</div>
					) : (
						  <SelectControl
							  label={ __( 'Form', 'groundhogg' ) }
							  value={ formId }
							  options={ forms }
							  onChange={ ( value ) => {
								  const form = window.Groundhogg.stores.forms.get( parseInt( value ) )
								  setAttributes( {
									  formId: value,
									  accentColor: form.settings.accentColor,
									  theme: form.settings.theme
								  } )
								} }
							  help={ __(
								  'Select the form to embed',
								  'groundhogg'
							  ) }
						  />
					  ) }
				</PanelBody>
				<PanelBody
					title={ __( 'Appearance', 'groundhogg' ) }
					initialOpen={ true }
				>
					<SelectControl
						label={ __( 'Theme', 'groundhogg' ) }
						value={ theme }
						options={ [
							{
								label: __( 'Default', 'groundhogg' ),
								value: 'default',
							},
							{
								label: __( 'Simple', 'groundhogg' ),
								value: 'simple',
							},
							{
								label: __( 'Modern', 'groundhogg' ),
								value: 'modern',
							},
							{
								label: __( 'Classic', 'groundhogg' ),
								value: 'classic',
							},
						] }
						onChange={ ( value ) =>
							setAttributes( { theme: value } )
						}
						help={ __(
							'Select the visual theme for the form',
							'groundhogg'
						) }
					/>
					<p style={{ marginTop: '16px', marginBottom: '8px' }}>
						{ __( 'Accent Color', 'groundhogg' ) }
					</p>
					<ColorPalette
						value={ accentColor }
						onChange={ ( value ) =>
							setAttributes( { accentColor: value || '#0073aa' } )
						}
					/>
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				{ ! formId ? (
					<div className="gh-form-shortcode-placeholder">
						<p>
							{ sprintf( __(
								'Embed a %s Form',
								'groundhogg'
							), window.Groundhogg.whiteLabelName ) }
						</p>
						<p>
							{ __(
								'Select a form in the block settings to get started.',
								'groundhogg'
							) }
						</p>
					</div>
				) : (
					<div className="gh-form-shortcode-preview">
						{ isLoading ? (
							<div className="gh-form-shortcode-loading">
								{ __( 'Loading form preview...', 'groundhogg' ) }
							</div>
						) : (
							<div
								dangerouslySetInnerHTML={ {
									__html: preview,
								} }
							/>
						) }
					</div>
				) }
			</div>
		</>
	);
}
